import { ARTIFACTS_PATH, type ControlReferenceResource, type ReviewCandidateResource } from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { Failure } from '../../shell/EmptyState.js';
import { Link } from '../../shell/router.js';
import { useReview } from './useReview.js';

/**
 * One Candidate inside a representative listing.
 *
 * The comparison is a direct switch, not a diff and not a slider: the operator sees the
 * listing as it is today and the listing as it would be. Sequences stay sequences and
 * video stays video - a set reduced to its first frame is not what a store shows, and
 * judging it that way is how a weak second frame ships.
 */
export function PlayStage({ experimentId, candidateId }: { experimentId: string; candidateId: string }): ReactNode {
  const open = useReview(experimentId);
  const [showing, setShowing] = useState<'candidate' | 'control'>('candidate');

  if (open.error !== null) {
    return (
      <section className="page">
        <Failure message={open.error} onRetry={open.reload} />
      </section>
    );
  }
  const review = open.value;
  if (review === null) return <section className="page" aria-busy="true" />;

  const candidate = review.candidates.find((entry) => entry.candidateId === candidateId) ?? review.candidates[0];
  if (candidate === undefined) {
    return (
      <section className="page">
        <Failure message="Этого Candidate нет в этом Experiment." />
      </section>
    );
  }

  return (
    <section className="page play-stage">
      <div className="page-head">
        <div>
          <p className="eyebrow">Play stage · превью внутри типичного листинга</p>
          <h1>{candidate.label}</h1>
          <p className="note">{candidate.summary}</p>
        </div>
        <div className="view-toggle" role="group" aria-label="Control или Candidate">
          <button type="button" aria-pressed={showing === 'control'} onClick={() => setShowing('control')}>
            Control
          </button>
          <button type="button" aria-pressed={showing === 'candidate'} onClick={() => setShowing('candidate')}>
            Candidate
          </button>
        </div>
      </div>

      <section className="listing-card" aria-label="Превью листинга Google Play">
        <header className="listing-head">
          <strong>{review.appDisplayName}</strong>
          <span className="note">{review.packageName ?? 'Ещё не опубликовано'}</span>
        </header>
        {showing === 'candidate' ? (
          <CandidateMedia candidate={candidate} />
        ) : (
          <ControlMedia control={review.control} />
        )}
      </section>

      <div className="row-actions">
        <Link to={`/experiments/${experimentId}/compare`} className="secondary-link">
          К Contact sheet
        </Link>
        <Link to={`/experiments/${experimentId}/export`} className="primary-link">
          Packing desk
        </Link>
      </div>
    </section>
  );
}

function CandidateMedia({ candidate }: { candidate: ReviewCandidateResource }): ReactNode {
  return (
    <div className="listing-assets" aria-label={`${candidate.label} на месте`}>
      {candidate.files.map((file) =>
        file.mediaType.startsWith('video/') ? (
          <video key={file.artifactId} className="frame" controls src={`${ARTIFACTS_PATH}/${file.artifactId}`}>
            <track kind="captions" />
          </video>
        ) : (
          <img
            key={file.artifactId}
            className="frame"
            src={`${ARTIFACTS_PATH}/${file.artifactId}`}
            alt={`${candidate.label}, ${file.label}`}
          />
        ),
      )}
    </div>
  );
}

/**
 * The Control, or the plain fact that there is none. A missing Control blocks nothing:
 * an Experiment whose listing was never captured is still worth running.
 */
function ControlMedia({ control }: { control: ControlReferenceResource | null }): ReactNode {
  if (control === null) {
    return <p className="note">Control не зафиксирован. Для этого Experiment это ничего не блокирует.</p>;
  }
  return (
    <>
      {/* Beside the strip rather than inside it. The strip is a row that scrolls sideways,
          and a sentence in that row is one more thing to scroll past to reach slide two. */}
      <p className="note">
        {control.source === 'controlSnapshot'
          ? 'Замороженный Control этого Experiment.'
          : 'Превью из последнего просмотренного Listing Snapshot. Просмотр ничего не замораживает.'}
      </p>
      <div className="listing-assets" aria-label="Текущий листинг">
        {control.items.map((item) =>
          item.artifactId === null ? null : (
            <img
              key={item.artifactId}
              className="frame"
              src={`${ARTIFACTS_PATH}/${item.artifactId}`}
              alt="Ассет текущего листинга"
            />
          ),
        )}
      </div>
    </>
  );
}
